import { useState } from 'react';
import { AlertTriangle, FileText, Image, Video, Mail, Mic, BookOpen } from 'lucide-react';
import type { Asset, AssetChannelPermission } from '../../types/assetRegistry';
import { StatusBadge, AssetTypeBadge, BrandBadge } from './Badges';

function AssetTypeIcon({ type }: { type: Asset['asset_type'] }) {
  switch (type) {
    case 'image':           return <Image size={28} color="#d1d5db" />;
    case 'video':           return <Video size={28} color="#d1d5db" />;
    case 'copy':            return <FileText size={28} color="#d1d5db" />;
    case 'email_template':  return <Mail size={28} color="#d1d5db" />;
    case 'podcast':         return <Mic size={28} color="#d1d5db" />;
    case 'training_module': return <BookOpen size={28} color="#d1d5db" />;
    default:                return <FileText size={28} color="#d1d5db" />;
  }
}

interface AssetCardProps {
  asset: Asset;
  permissions?: AssetChannelPermission[];
  onClick: () => void;
}

export function AssetCard({ asset, permissions = [], onClick }: AssetCardProps) {
  const [hovered, setHovered] = useState(false);
  const hasWarning = permissions.some(p => p.permission_type === 'restricted' || p.permission_type === 'exclusive');

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%', textAlign: 'left', background: '#fff', borderRadius: 12,
        overflow: 'hidden', border: `1.5px solid ${hovered ? '#1B4B8A' : '#e5e7eb'}`,
        boxShadow: hovered ? '0 4px 16px rgba(27,75,138,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'all 0.2s', cursor: 'pointer', padding: 0,
      }}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative', aspectRatio: '16/9', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {asset.thumbnail_url ? (
          <img
            src={asset.thumbnail_url}
            alt={asset.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: hovered ? 'scale(1.04)' : 'scale(1)', transition: 'transform 0.3s' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <AssetTypeIcon type={asset.asset_type} />
          </div>
        )}
        {hasWarning && (
          <div style={{ position: 'absolute', top: 8, right: 8, background: '#f59e0b', borderRadius: '50%', padding: 4, display: 'flex' }} title="Has restricted or exclusive permissions">
            <AlertTriangle size={11} color="#fff" />
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {asset.title}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <AssetTypeBadge type={asset.asset_type} />
          <BrandBadge brand={asset.brand_entity} />
          <StatusBadge status={asset.status} />
        </div>
      </div>
    </button>
  );
}
